import {
  HttpContextToken,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { InjectionToken } from '@angular/core';

export type HttpFeedbackSeverity = 'success' | 'info' | 'warning' | 'error';

export interface HttpFeedbackPresenterPayload {
  message: string;
  status?: number;
  severity: HttpFeedbackSeverity;
  response: HttpResponse<any> | HttpErrorResponse;
  isError: boolean;
}

export interface HttpFeedbackPresenterMeta {
  config: ResolvedHttpFeedbackConfig;
  context?: HttpFeedbackRequestContext | null;
}

export type HttpFeedbackPresenter = (
  payload: HttpFeedbackPresenterPayload,
  meta: HttpFeedbackPresenterMeta
) => void;

export interface HttpFeedbackConfig {
  enabled?: boolean;
  handleSuccess?: boolean;
  handleError?: boolean;
  headerStatusCodeKey?: string;
  bodyMessagePaths?: string[][];
  bodyStatusCodePaths?: string[][];
  severityResolver?: HttpFeedbackSeverityResolver;
  presenter?: HttpFeedbackPresenter;
  toastOptions?: Partial<Record<HttpFeedbackSeverity, any>>;
}

export interface HttpFeedbackRequestContext
  extends Partial<HttpFeedbackConfig> {
  skip?: boolean;
  message?: string;
  severity?: HttpFeedbackSeverity;
  status?: number;
}

export interface ResolvedHttpFeedbackConfig extends HttpFeedbackConfig {
  enabled: boolean;
  handleSuccess: boolean;
  handleError: boolean;
  headerStatusCodeKey: string;
  bodyMessagePaths: string[][];
  bodyStatusCodePaths: string[][];
  toastOptions: Partial<Record<HttpFeedbackSeverity, any>>;
  severityResolver: HttpFeedbackSeverityResolver;
}

export interface HttpFeedbackSeverityResolverInput {
  status?: number;
  response: HttpResponse<any> | HttpErrorResponse;
  isError: boolean;
}

export type HttpFeedbackSeverityResolver = (
  input: HttpFeedbackSeverityResolverInput
) => HttpFeedbackSeverity;

const defaultSeverityResolver: HttpFeedbackSeverityResolver = ({
  status,
  isError,
}) => {
  if (isError) {
    if (status && status >= 500) {
      return 'error';
    }
    return 'warning';
  }

  if (status && status >= 200 && status < 300) {
    return 'success';
  }

  return 'info';
};

const DEFAULT_STATUS_PATHS: string[][] = [
  ['data', 'StatusCode'],
  ['data', 'statusCode'],
  ['statusCode'],
  ['StatusCode'],
  ['status'],
  ['headers', 'statusCode'],
];

const DEFAULT_MESSAGE_PATHS: string[][] = [
  ['data', 'userMessage'],
  ['data', 'UserMessage'],
  ['data', 'message'],
  ['data', 'Message'],
  ['message'],
  ['Message'],
  ['error', 'message'],
  ['error', 'Message'],
];

export const DEFAULT_HTTP_FEEDBACK_CONFIG: ResolvedHttpFeedbackConfig = {
  enabled: true,
  handleSuccess: false,
  handleError: true,
  headerStatusCodeKey: 'statusCode',
  bodyMessagePaths: DEFAULT_MESSAGE_PATHS,
  bodyStatusCodePaths: DEFAULT_STATUS_PATHS,
  toastOptions: {},
  severityResolver: defaultSeverityResolver,
};

export const HTTP_FEEDBACK_CONFIG = new InjectionToken<HttpFeedbackConfig>(
  'HTTP_FEEDBACK_CONFIG',
  {
    providedIn: 'root',
    factory: () => DEFAULT_HTTP_FEEDBACK_CONFIG,
  }
);

export const HTTP_FEEDBACK_CONTEXT = new HttpContextToken<
  HttpFeedbackRequestContext | null
>(() => null);
